/**
 * The pieces an M7 rail conversation is built from. Kept small and dumb: the
 * conversation decides what goes where, these decide how it looks.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FC,
  type MouseEvent,
  type ReactNode,
  type SVGProps,
} from "react";
import { AlertCircle, Bot, Check, ChevronRight, Copy, Loader2, TriangleAlert } from "lucide-react";
import type { ChatContextUsage, MessageThinking } from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import ClaudeIcon from "@/assets/icons/brands/claude.svg?react";
import OpenAiIcon from "@/assets/icons/brands/openai.svg?react";
import GeminiIcon from "@/assets/icons/brands/google_gemini.svg?react";
import KimiIcon from "@/assets/icons/brands/kimi.svg?react";
import SparkleIcon from "@/assets/icons/magic_star.svg?react";
import { getChatModelProvider } from "@/hooks/useChatModels";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import StreamingLineReveal from "@/app/routes/dashboard/components/StreamingLineReveal";
import AgentMark from "@/app/routes/dashboard/components/icons/AgentMark";
import { ChatTextWithUuidPills } from "@/app/routes/dashboard/components/chatUuidPills";
import { formatToolPayload } from "@/app/routes/dashboard/components/chatRailLedger";
import { getToolIcon } from "@/app/routes/dashboard/components/toolCallDisplay";
import {
  groupConsecutiveTools,
  type RailToolItem,
} from "@/app/routes/dashboard/components/railTools";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import { isDevMode } from "@/config/envvars";
import { clusterLabel, messageTime, sentenceCaseToolLabel } from "./railFormat";
import { splitTrailingLinks } from "./railLinks";
import { LinkEmbeds, SentFiles } from "./RailMedia";

export function RailMark() {
  return <AgentMark className="m7r-mark" aria-hidden />;
}

export function ClusterTime({ iso }: { iso: string }) {
  const label = clusterLabel(iso);
  if (!label) return null;
  return (
    <time className="m7r-time" dateTime={iso}>
      {label}
    </time>
  );
}

/* --- the model's mark ------------------------------------------------------------ */

const PROVIDER_MARKS: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  Anthropic: ClaudeIcon,
  OpenAI: OpenAiIcon,
  Google: GeminiIcon,
  Moonshot: KimiIcon,
};

/**
 * The maker's mark for a model, in one colour (the text colour it sits in), so
 * the six Mohtawa colours stay the only colours on the rail. Auto keeps its
 * sparkle: the agent picks the model.
 */
export function ModelMark({ modelId, className }: { modelId: string | null | undefined; className?: string }) {
  if (!modelId) return null;
  const Mark = modelId === "auto" ? SparkleIcon : PROVIDER_MARKS[getChatModelProvider(modelId)];
  const classes = `m7r-model-mark${className ? ` ${className}` : ""}`;
  if (!Mark) return <Bot className={classes} aria-hidden strokeWidth={1.75} />;
  return <Mark className={classes} aria-hidden data-auto={modelId === "auto" || undefined} />;
}

/* --- time and copy, on hover ------------------------------------------------------ */

/**
 * A phone has no hover: a tap on the message shows its time and Copy, a second
 * tap hides them. Taps on a link, a button or a field inside it are theirs.
 */
export function useTapToReveal() {
  const [shown, setShown] = useState(false);
  const onClick = (event: MouseEvent<HTMLElement>) => {
    if (!window.matchMedia("(hover: none)").matches) return;
    if ((event.target as HTMLElement).closest("a, button, input, textarea, summary")) return;
    if (window.getSelection()?.toString()) return;
    setShown((value) => !value);
  };
  return { shown, onClick };
}

/** The message's own time, and Copy, which says Copied for a moment. */
function HoverActions({ iso, text, label }: { iso: string; text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <span className="m7r-hover">
      <time dateTime={iso}>{messageTime(iso)}</time>
      <button
        type="button"
        className="m7r-copy"
        data-copied={copied || undefined}
        aria-label={copied ? "Copied" : label}
        title={copied ? "Copied" : "Copy"}
        onClick={() => {
          void navigator.clipboard
            ?.writeText(text)
            .then(() => setCopied(true))
            /* A denied clipboard is not a copy; saying nothing beats a false "copied". */
            .catch(() => {});
        }}
      >
        {copied ? (
          <>
            <Check aria-hidden strokeWidth={2} />
            <span>Copied</span>
          </>
        ) : (
          <Copy aria-hidden strokeWidth={1.75} />
        )}
      </button>
    </span>
  );
}

/* --- your message ---------------------------------------------------------------- */

/**
 * Your words in their plane. The files you sent with them stack above, and a
 * link you ended on becomes its embed, so the bubble drops the address. Past
 * eight lines the bubble folds, with Show all.
 */
export function HumanBubble({
  id,
  text,
  createdAt,
  files = [],
  onRetryFile,
  lead,
  corner,
}: {
  id: string;
  text: string;
  createdAt: string;
  files?: ChatAttachmentItem[];
  onRetryFile?: (documentId: string) => void;
  /** Sits on top of the words in the bubble (the Workshop's Quick Create pill). */
  lead?: ReactNode;
  /** Overlaps a corner of the bubble (a shared thread's seen mark). */
  corner?: ReactNode;
}) {
  const { body, links } = splitTrailingLinks(text);
  const tap = useTapToReveal();
  const textRef = useRef<HTMLDivElement>(null);
  const [folds, setFolds] = useState(false);
  const [open, setOpen] = useState(false);

  // Measured, not counted: eight lines depends on the rail's width and the font.
  useEffect(() => {
    const el = textRef.current;
    if (!el || open) return;
    const measure = () => setFolds(el.scrollHeight > el.clientHeight + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [body, open]);

  return (
    <div className="m7r-you" data-show={tap.shown || undefined} onClick={tap.onClick}>
      <SentFiles items={files} onRetry={onRetryFile} />
      {links.length > 0 ? (
        <div data-msg-id={body ? undefined : id}>
          <LinkEmbeds links={links} side="you" />
        </div>
      ) : null}
      {body || lead ? (
        <div className="m7r-bubble" data-msg-id={id}>
          {lead}
          <div ref={textRef} className="m7r-bubble-text" data-open={open || undefined}>
            <ChatTextWithUuidPills text={body} />
          </div>
          {folds || open ? (
            <button
              type="button"
              className="m7r-show-all"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? "Show less" : "Show all"}
            </button>
          ) : null}
          {corner}
        </div>
      ) : corner ? (
        <div className="m7r-bubble" data-msg-id={id}>
          {corner}
        </div>
      ) : null}
      <HoverActions iso={createdAt} text={text} label="Copy your message" />
    </div>
  );
}

function formatThinkingTime(ms: number): string | null {
  if (!ms || ms <= 0) return null;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * "Thought for 0.4s", opening onto what the agent thought. The text is markdown:
 * a bold heading then its paragraph, so it renders as titled paragraphs rather
 * than raw asterisks, and nobody counts a heading as a step.
 */
export function ThinkingRow({
  thinking,
  withMark,
  live = false,
}: {
  thinking: MessageThinking | null;
  withMark: boolean;
  live?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const time = thinking ? formatThinkingTime(thinking.durationMs) : null;
  const text = thinking ? scrubVendorTerms(thinking.text).trim() : "";
  const canOpen = text.length > 0;
  const label = live ? "Thinking" : time ? `Thought for ${time}` : "Thought it through";

  return (
    <>
      <div className="m7r-mark-row">
        {withMark ? <RailMark /> : null}
        {canOpen ? (
          <button
            type="button"
            className="m7r-think"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((value) => !value)}
          >
            <span className={live ? "m7r-shimmer" : undefined}>{label}</span>
            <ChevronRight aria-hidden strokeWidth={1.75} />
          </button>
        ) : (
          <span className={live ? "m7r-think m7r-shimmer" : "m7r-think"} role={live ? "status" : undefined}>
            {label}
          </span>
        )}
      </div>
      {canOpen ? (
        <div className="m7r-thought" data-open={open || undefined} id={bodyId}>
          <div>
            <div className="m7r-thought-body">
              {open ? <ChatMarkdown content={text} /> : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** A turn with no thinking still opens on the mark, so every reply starts the same way. */
export function MarkRow() {
  return (
    <div className="m7r-mark-row">
      <RailMark />
    </div>
  );
}

/**
 * A page's own question at the end of the chat (agentRail.ask): the person's
 * line as their bubble, then the card under M7's mark. Nothing reaches the
 * model; the card is answered here and runs the page's own action.
 */
export function LocalAskTurn({ prompt, children }: { prompt: string; children: ReactNode }) {
  const [createdAt] = useState(() => new Date().toISOString());
  return (
    <>
      <HumanBubble id="local-ask" text={prompt} createdAt={createdAt} />
      <div className="m7r-turn">
        <MarkRow />
        {children}
      </div>
    </>
  );
}

function ToolStatus({ status }: { status: RailToolItem["status"] }) {
  return (
    <span className="m7r-tool-status" data-status={status}>
      {status === "run" ? (
        <>
          <Loader2 className="animate-spin" aria-hidden strokeWidth={2} />
          <span className="co-sr-only">Running</span>
        </>
      ) : status === "err" ? (
        <>
          <AlertCircle aria-hidden strokeWidth={1.75} />
          <span className="co-sr-only">Failed</span>
        </>
      ) : (
        <>
          <Check aria-hidden strokeWidth={2} />
          <span className="co-sr-only">Done</span>
        </>
      )}
    </span>
  );
}

function ToolRow({ item, count }: { item: RailToolItem; count: number }) {
  const [open, setOpen] = useState(false);
  const { icon: Icon } = getToolIcon(item.event.toolName);
  const label = sentenceCaseToolLabel(item.label);
  const payload = isDevMode
    ? [
        formatToolPayload(item.event.input) && `IN\n${formatToolPayload(item.event.input)}`,
        formatToolPayload(item.event.result) && `OUT\n${formatToolPayload(item.event.result)}`,
      ]
        .filter(Boolean)
        .join("\n\n")
    : "";
  const inspectable = Boolean(payload) && item.status !== "run";

  const row = (
    <>
      <Icon className="m7r-tool-icon" aria-hidden strokeWidth={1.75} />
      <span className="m7r-tool-label">{label}</span>
      {count > 1 ? <span className="m7r-tool-count">×{count}</span> : null}
      <ToolStatus status={item.status} />
    </>
  );

  return (
    <li>
      {inspectable ? (
        <button
          type="button"
          className="m7r-tool"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {row}
        </button>
      ) : (
        <div className="m7r-tool">{row}</div>
      )}
      {open && inspectable ? <pre className="m7r-tool-payload">{payload}</pre> : null}
    </li>
  );
}

/**
 * What the agent did, one quiet row per tool. Repeats collapse to ×N; dev builds
 * keep every call and let you open its payload.
 */
export function ToolRows({ tools }: { tools: RailToolItem[] }) {
  if (tools.length === 0) return null;
  const rows = isDevMode
    ? tools.map((item) => ({ ...item, count: 1 }))
    : groupConsecutiveTools(tools);
  return (
    <ul className="m7r-tools" aria-label="What the agent did">
      {rows.map((item) => (
        <ToolRow key={item.key} item={item} count={item.count} />
      ))}
    </ul>
  );
}

export function ReplyText({ content }: { content: string }) {
  return (
    <div className="m7r-reply">
      <ChatMarkdown content={content} />
    </div>
  );
}

export function StreamingText({ content, live }: { content: string; live: boolean }) {
  return (
    <div className="m7r-reply">
      <StreamingLineReveal content={content} live={live} />
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 100_000 ? 0 : 1)}k`;
  return String(Math.round(n));
}

/**
 * Under a reply: the model that wrote it, always. Its time and Copy come in on
 * hover, after the credit, so the credit never moves. Token counts are for dev
 * builds.
 */
export function ReplyFoot({
  text,
  model,
  modelId,
  createdAt,
  usage,
}: {
  text: string;
  model: string | null;
  modelId: string | null;
  createdAt: string;
  usage: ChatContextUsage | null;
}) {
  return (
    <div className="m7r-foot">
      {model ? (
        <span className="m7r-model" title={`Answered by ${model}`}>
          <ModelMark modelId={modelId} />
          <span className="m7r-model-name">{model}</span>
        </span>
      ) : null}
      {isDevMode && usage ? (
        <span className="m7r-model" title="Dev builds only">
          {formatTokens(usage.inputTokens)} in · {formatTokens(usage.outputTokens)} out
        </span>
      ) : null}
      <HoverActions iso={createdAt} text={text} label="Copy this reply" />
    </div>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="m7r-notice" role="separator">
      {children}
    </div>
  );
}

export function StreamError({ message }: { message: string }) {
  return (
    <div className="m7r-error" role="alert">
      <TriangleAlert aria-hidden strokeWidth={1.75} />
      <span>{scrubVendorTerms(message)}</span>
    </div>
  );
}
